import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getGiftCardsBySeller, cancelGiftCard } from '../../firebase/giftcards';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Navbar from '../Navbar';
import { formatPrice } from '../../utils/formatPrice';

const REQUIREMENT_LABELS = {
  'contact.phonenumber.required': { label: 'Voeg je telefoonnummer toe', actionLabel: 'Toevoegen' },
  'contact.phonenumber.verification.required': { label: 'Voeg je telefoonnummer toe', actionLabel: 'Voltooien' },
  'bank_account.required': { label: 'Voeg je bankrekening toe', actionLabel: 'Toevoegen' },
  'bank_account.verification.required': { label: 'Voeg je bankrekening toe', actionLabel: 'Voltooien' },
  'contact.verification.required': { label: 'Verifieer je identiteit', actionLabel: 'Start iDIN' },
  'identity.required': { label: 'Verifieer je identiteit', actionLabel: 'Voltooien' },
};
const PHONE_REQUIREMENT_TYPES = new Set([
  'contact.phonenumber.required',
  'contact.phonenumber.verification.required',
]);
const BANK_REQUIREMENT_TYPES = new Set([
  'bank_account.required',
  'bank_account.verification.required',
]);
const IDENTITY_REQUIREMENT_TYPES = new Set([
  'contact.verification.required',
  'identity.required',
]);

const isPhoneRequirement = (type) => PHONE_REQUIREMENT_TYPES.has(type);
const isBankRequirement = (type) => BANK_REQUIREMENT_TYPES.has(type);
const isIdentityRequirement = (type) => IDENTITY_REQUIREMENT_TYPES.has(type);

const BANK_STATUS_LABELS = {
  new: 'Nieuw',
  pending: 'In behandeling',
  verified: 'Geverifieerd',
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
  disapproved: 'Afgewezen',
};
const REPLACEABLE_BANK_STATUSES = new Set(['rejected', 'disapproved']);
const BANK_DISAPPROVAL_LABELS = {
  bank_account_business:
    'Je hebt een zakelijke rekening ingevuld. We hebben een particuliere rekening nodig.',
  bank_account_creditcard_without_iban:
    'We kunnen geen IBAN vinden op je bankafschrift.',
  bank_account_document_both_sides_needed:
    'We hebben de voor- én achterkant van je bankafschrift nodig.',
  bank_account_document_expired:
    'Je bankafschrift is te oud. Upload een afschrift van maximaal 3 maanden oud.',
  bank_account_document_front_missing:
    'De voorkant van je bankafschrift ontbreekt.',
  bank_account_document_incorrect:
    'Dit bankafschrift hoort niet bij de opgegeven bankrekening.',
  bank_account_from_unlicensed_country:
    'We kunnen helaas niet uitbetalen naar een bankrekening uit dit land.',
  bank_account_iban_different_from_document:
    'De IBAN op je bankafschrift is anders dan de IBAN die je hebt ingevuld.',
  bank_account_is_not_allowed:
    'Deze bankrekening kunnen we helaas niet gebruiken voor je uitbetaling.',
  bank_account_joint_not_allowed:
    'Een gezamenlijke rekening kunnen we hiervoor helaas niet gebruiken.',
  other_reason:
    'Onze betaalpartner heeft je bankrekening afgekeurd. Upload een ander afschrift of probeer een andere rekening.',
};

const getBankStatusLabel = (status) => {
  if (!status) return null;
  return BANK_STATUS_LABELS[status] || status;
};

const getBankDisapprovalLabel = (reason) => (
  BANK_DISAPPROVAL_LABELS[reason] || `OPP reden: ${reason}`
);

const BankAccountInput = ({
  onSubmit,
  onCancel,
  title = 'Bankrekening koppelen',
  description = 'Voer je IBAN in voor uitbetalingen',
  buttonLabel = 'Koppelen',
}) => {
  const [iban, setIban] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    const cleaned = iban.replace(/\s/g, '').toUpperCase();
    if (!cleaned.startsWith('NL') || cleaned.length !== 18) {
      setError('Voer een geldig Nederlands IBAN in (bijv. NL91 ABNA 0417 1643 00)');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const connectBankAccount = httpsCallable(functionsInstance, 'connectBankAccount');
      const result = await connectBankAccount({ iban: cleaned });

      if (result.data.verificationUrl) {
        window.location.href = result.data.verificationUrl;
        return;
      }
      onSubmit();
    } catch (err) {
      setError(err.message || 'Er ging iets mis. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
      <div className="flex items-center space-x-3 mb-3">
        <span className="text-xl">🏦</span>
        <div>
          <p className="font-semibold text-gray-900">{title}</p>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <input
          type="text"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="NL91 ABNA 0417 1643 00"
          className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none font-mono"
          disabled={loading}
        />
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 rounded-lg border-2 border-gray-300 text-sm font-semibold text-gray-700 transition hover:border-orange-400 disabled:opacity-50"
          >
            Terug
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || !iban.trim()}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-50"
        >
          {loading ? 'Bezig...' : buttonLabel}
        </button>
      </div>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </div>
  );
};

const PhoneNumberModal = ({ onClose, onSuccess }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const cleanPhone = phone.replace(/\s/g, '');
  const isValidPhone = /^\+31[1-9]\d{8}$/.test(cleanPhone);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!isValidPhone) {
      setError('Voer een geldig Nederlands telefoonnummer in, bijvoorbeeld +31612345678.');
      return;
    }

    setLoading(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const addMerchantPhone = httpsCallable(functionsInstance, 'addMerchantPhone');
      await addMerchantPhone({ phone: cleanPhone });
      await onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'We konden je telefoonnummer niet toevoegen. Probeer het opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-5">
          <h3 className="text-xl font-bold text-gray-900">Telefoonnummer toevoegen</h3>
          <p className="mt-2 text-sm text-gray-600">
            Vul je telefoonnummer in. We gebruiken het alleen voor je uitbetaling.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="mb-2 block text-sm font-semibold text-gray-900">
            Nederlands telefoonnummer
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+31612345678"
            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 font-mono focus:border-orange-500 focus:outline-none"
            disabled={loading}
          />
          <p className="mt-2 text-xs text-gray-500">Gebruik +31, zonder spaties mag ook.</p>

          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 rounded-lg border-2 border-gray-300 px-4 py-3 font-semibold text-gray-700 transition hover:border-red-500 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-lg bg-orange-500 px-4 py-3 font-semibold text-white transition hover:bg-orange-600 disabled:opacity-50"
            >
              {loading ? 'Bezig...' : 'Toevoegen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};




const ComplianceSection = ({ merchantData, onRefresh, hasSellerActivity, error }) => {
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showBankInput, setShowBankInput] = useState(false);

  if (error) {
    return (
      <div className="mb-8 rounded-lg bg-white p-5 shadow">
        <h2 className="text-xl font-bold text-gray-900">Gegevens voor uitbetaling</h2>
        <p className="mt-2 text-sm text-gray-600">
          We konden je uitbetalingsgegevens niet ophalen. Je dashboard blijft werken; probeer straks opnieuw te verversen.
        </p>
      </div>
    );
  }

  if (!merchantData) return null;

  if (merchantData.status === 'no_merchant') {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-2">Gegevens voor uitbetaling</h2>
        <p className="text-gray-600">Je verkopersprofiel wordt aangemaakt wanneer je je eerste cadeaubon aanbiedt.</p>
      </div>
    );
  }

  const requirements = merchantData.compliance?.requirements || [];
  const openRequirements = requirements.filter(r => r.status !== 'verified');
  const phoneRequirement = openRequirements.find(req => isPhoneRequirement(req.type));
  const bankRequirement = openRequirements.find(req => isBankRequirement(req.type));
  const identityRequirement = openRequirements.find(req => isIdentityRequirement(req.type));
  const hasBankAccount = Boolean(merchantData.bankAccount);
  const hasPhone = Boolean(merchantData.phone) && !phoneRequirement;
  const phoneNeedsHostedVerification = phoneRequirement?.type === 'contact.phonenumber.verification.required';
  const bankNeedsHostedVerification = bankRequirement?.type === 'bank_account.verification.required';
  const idinIsPending = identityRequirement?.status === 'pending' || merchantData.contact?.status === 'pending';
  const showIdinTask = Boolean(identityRequirement) || idinIsPending;
  const bankStatus = merchantData.bankAccount?.status || bankRequirement?.status || null;
  const bankStatusLabel = getBankStatusLabel(bankStatus);
  const bankIsPending = bankRequirement?.status === 'pending' || merchantData.bankAccount?.status === 'pending';
  const bankCanBeReplaced = REPLACEABLE_BANK_STATUSES.has(bankStatus);
  const bankDisapprovalReason = merchantData.bankAccount?.disapprovals?.find((item) => item?.name)?.name;
  const bankAccountLabel = merchantData.bankAccount?.iban
    ? `Gekoppeld: ${merchantData.bankAccount.iban}`
    : hasBankAccount
      ? 'Bankrekening toegevoegd'
      : bankIsPending
        ? 'Bankrekening ontvangen'
        : null;
  const bankDetailBase = bankAccountLabel
    ? `${bankAccountLabel}${bankStatusLabel ? `. Status: ${bankStatusLabel}.` : '.'}`
    : 'Naar deze rekening maken we het geld over.';
  const bankDetail = bankCanBeReplaced
    ? [
      'Deze bankrekening is afgewezen.',
      bankDisapprovalReason ? `Reden: ${getBankDisapprovalLabel(bankDisapprovalReason)}` : null,
      'Voer een andere IBAN in om uitbetalingen te ontvangen.',
    ].filter(Boolean).join(' ')
    : bankDetailBase;
  const bankSupportingDetail = bankCanBeReplaced && bankAccountLabel
    ? `Afgewezen rekening: ${bankAccountLabel.replace(/^Gekoppeld: /, '')}`
    : null;
  const otherRequirements = openRequirements.filter(req =>
    !isPhoneRequirement(req.type) && !isBankRequirement(req.type) && !isIdentityRequirement(req.type)
  );
  const tasks = [
    {
      id: 'phone',
      type: phoneRequirement?.type || 'contact.phonenumber.required',
      label: REQUIREMENT_LABELS[phoneRequirement?.type]?.label || 'Voeg je telefoonnummer toe',
      actionLabel: REQUIREMENT_LABELS[phoneRequirement?.type]?.actionLabel || 'Toevoegen',
      complete: hasPhone,
      detail: hasPhone ? `Gekoppeld: ${merchantData.phone?.masked || 'telefoonnummer toegevoegd'}` : 'Hebben we nodig om je uitbetaling te kunnen verwerken.',
      onAction: phoneNeedsHostedVerification ? null : () => setShowPhoneModal(true),
      redirectUrl: phoneNeedsHostedVerification ? phoneRequirement?.redirectUrl : null,
    },
    {
      id: 'bank',
      type: bankRequirement?.type || 'bank_account.required',
      label: bankCanBeReplaced ? 'Bankrekening afgewezen' : REQUIREMENT_LABELS[bankRequirement?.type]?.label || 'Voeg je bankrekening toe',
      actionLabel: bankIsPending ? 'In behandeling' : bankCanBeReplaced ? 'Nieuwe bankrekening invoeren' : REQUIREMENT_LABELS[bankRequirement?.type]?.actionLabel || 'Toevoegen',
      complete: hasBankAccount && !bankRequirement && !bankIsPending && !bankCanBeReplaced,
      detail: bankDetail,
      supportingDetail: bankSupportingDetail,
      onAction: bankIsPending || (bankNeedsHostedVerification && !bankCanBeReplaced) ? null : () => setShowBankInput((value) => !value),
      redirectUrl: bankNeedsHostedVerification && !bankIsPending && !bankCanBeReplaced ? bankRequirement?.redirectUrl : null,
      statusLabel: bankIsPending ? 'In behandeling' : null,
      needsAction: !bankIsPending,
    },
    ...(showIdinTask ? [{
      id: 'identity',
      type: identityRequirement?.type || 'contact.verification.required',
      label: REQUIREMENT_LABELS[identityRequirement?.type]?.label || 'Verifieer je identiteit',
      actionLabel: idinIsPending ? 'In behandeling' : REQUIREMENT_LABELS[identityRequirement?.type]?.actionLabel || 'Start iDIN',
      complete: false,
      detail: idinIsPending
        ? 'Je iDIN-verificatie is ontvangen. OPP controleert je gegevens.'
        : 'We hebben nog een iDIN-check nodig voordat we kunnen uitbetalen.',
      redirectUrl: idinIsPending ? null : identityRequirement?.redirectUrl || merchantData.contact?.verificationUrl || null,
      statusLabel: idinIsPending ? 'In behandeling' : null,
      needsAction: !idinIsPending,
    }] : []),
    ...otherRequirements.map((req, index) => {
      const info = REQUIREMENT_LABELS[req.type] || {
        label: req.type,
        actionLabel: req.redirectUrl ? 'Voltooien' : 'Bekijk',
      };

      return {
        id: `${req.type}-${index}`,
        type: req.type,
        label: info.label,
        actionLabel: info.actionLabel,
        complete: false,
        detail: req.status === 'unverified' ? 'We hebben nog een extra check nodig.' : req.status,
        redirectUrl: req.redirectUrl,
      };
    }),
  ];
  const outstandingTasks = tasks.filter(task => !task.complete);
  const actionableTasks = outstandingTasks.filter(task => task.needsAction !== false);
  const completedTasks = tasks.filter(task => task.complete);
  const isReady = outstandingTasks.length === 0 && completedTasks.length > 0;
  const isWaitingForReview = outstandingTasks.length > 0 && actionableTasks.length === 0;
  const isProminent = (hasSellerActivity || bankCanBeReplaced) && actionableTasks.length > 0;
  const cardClass = isProminent
    ? 'mb-8 rounded-lg border-2 border-red-200 bg-white p-6 shadow'
    : 'mb-8 rounded-lg bg-white p-5 shadow';
  const sectionIntro = isReady
    ? 'Alles staat klaar. Wij regelen de rest.'
    : isWaitingForReview
      ? 'We controleren je gegevens. Je hoeft nu niets te doen.'
    : isProminent
      ? 'Bijna klaar! Vul nog even je gegevens aan, dan kunnen we de volgende werkdag uitbetalen.'
      : 'Voeg je telefoonnummer en bankrekening toe. Zo kunnen we je uitbetalen zodra je cadeaubon is verkocht.';

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Gegevens voor uitbetaling</h2>
            {isProminent && (
              <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">
                Actie nodig
              </span>
            )}
            {isReady && (
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
                Klaar voor uitbetaling ✓
              </span>
            )}
            {isWaitingForReview && (
              <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-700">
                In behandeling
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-600">
            {sectionIntro}
          </p>
        </div>
        <button
          onClick={onRefresh}
          className="text-sm text-gray-500 hover:text-gray-700 transition"
        >
          ↻ Ververs
        </button>
      </div>

      {tasks.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Setup je uitbetalingen wanneer je klaar bent.
        </div>
      )}

      {tasks.length > 0 && (
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.id}
              className={`rounded-lg border p-4 ${
                task.complete
                  ? 'border-green-100 bg-green-50'
                  : isProminent
                    ? 'border-red-200 bg-red-50'
                    : 'border-orange-200 bg-orange-50'
              }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className={`mt-1 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                    task.complete ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {task.complete ? '✓' : '!'}
                  </span>
                  <div className="min-w-0">
                    <p className={`font-semibold ${task.complete ? 'text-green-900' : 'text-gray-900'}`}>
                      {task.label}
                    </p>
                    <p className="text-sm text-gray-600">{task.detail}</p>
                    {task.supportingDetail && (
                      <p className="mt-1 text-xs text-gray-500">{task.supportingDetail}</p>
                    )}
                  </div>
                </div>

                {!task.complete && task.onAction && (
                  <button
                    type="button"
                    onClick={task.onAction}
                    className="w-full shrink-0 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 sm:w-auto"
                  >
                    {task.actionLabel}
                  </button>
                )}
                {!task.complete && task.redirectUrl && (
                  <a
                    href={task.redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full shrink-0 rounded-lg bg-orange-500 px-4 py-2 text-center text-sm font-semibold text-white transition hover:bg-orange-600 sm:w-auto"
                  >
                    {task.actionLabel} →
                  </a>
                )}
                {!task.complete && task.statusLabel && !task.onAction && !task.redirectUrl && (
                  <span className="rounded-full bg-yellow-100 px-3 py-1 text-sm font-semibold text-yellow-700">
                    {task.statusLabel}
                  </span>
                )}
              </div>

              {task.id === 'bank' && showBankInput && !task.complete && (
                <div className="mt-4">
                  <BankAccountInput
                    onSubmit={async () => {
                      setShowBankInput(false);
                      await onRefresh();
                    }}
                    onCancel={() => setShowBankInput(false)}
                    title={bankCanBeReplaced ? 'Andere bankrekening toevoegen' : 'Bankrekening toevoegen'}
                    description={bankCanBeReplaced ? 'Vul een andere IBAN in voor je uitbetalingen.' : 'Naar deze rekening maken we het geld over.'}
                    buttonLabel={bankCanBeReplaced ? 'Nieuwe rekening toevoegen' : 'Toevoegen'}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showPhoneModal && (
        <PhoneNumberModal
          onClose={() => setShowPhoneModal(false)}
          onSuccess={onRefresh}
        />
      )}
    </div>
  );
};

const UserDashboard = () => {
  const [user, setUser] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merchantData, setMerchantData] = useState(null);
  const [merchantError, setMerchantError] = useState('');
  const [verificationBanner, setVerificationBanner] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get('verified');
    if (verified === 'bank') {
      setVerificationBanner('Top, we hebben je bankrekening ontvangen. We controleren je gegevens even, duurt meestal een paar minuten.');
    } else if (verified === 'idin') {
      setVerificationBanner('Top, je gegevens zijn binnen. We controleren ze even — meestal binnen 24 uur klaar.');
    }
  }, [location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      setUser(currentUser);
      await loadListings(currentUser.uid);
      await loadMerchantStatus();
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const loadMerchantStatus = async () => {
    try {
      setMerchantError('');
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const getMerchantStatus = httpsCallable(functionsInstance, 'getMerchantStatus');
      const result = await getMerchantStatus();
      setMerchantData(result.data);
    } catch (error) {
      console.error('Error loading merchant status:', error);
      setMerchantError(error.message || 'Kon je uitbetalingsgegevens niet ophalen.');
    }
  };

  const loadListings = async (userId) => {
    setLoading(true);
    try {
      const userCards = await getGiftCardsBySeller(userId);
      const cardsWithPrivateData = await Promise.all(
        userCards.map(async (card) => {
          try {
            const cardDetailsDoc = await getDoc(doc(db, `giftcards/${card.id}/private/card_details`));
            const cardDetails = cardDetailsDoc.exists() ? cardDetailsDoc.data() : {};
            return { ...card, ...cardDetails };
          } catch (error) {
            console.error(`Error fetching card details for ${card.id}:`, error);
            return card;
          }
        })
      );
      setListings(cardsWithPrivateData);
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (listing) => {
  if (!window.confirm(`Weet je zeker dat je je ${listing.brand} cadeaubon wilt annuleren?`)) return;
  
  try {
    await cancelGiftCard(listing.id, user.uid);
    await loadListings(user.uid);
  } catch (error) {
    alert(error.message);
  }
};

  const getStatusBadge = (status) => {
    const styles = {
      pending_verification: 'bg-yellow-100 text-yellow-700',
      pendingAdminReview: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      active: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      sold: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-gray-100 text-gray-700',

    };
    const labels = {
      pending_verification: 'In afwachting',
      pendingAdminReview: 'In review',
      approved: 'Actief',
      active: 'Actief',
      rejected: 'Afgewezen',
      sold: 'Verkocht',
      cancelled: 'Geannuleerd',

    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Onbekend';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Laden...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mijn Account</h1>
            <p className="text-gray-600">Welkom terug, {user?.displayName || user?.email}</p>
          </div>
          <button
            onClick={() => { user && loadListings(user.uid); loadMerchantStatus(); }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            ↻ Ververs
          </button>
        </div>

        {verificationBanner && (
          <div className="mb-6 flex items-start justify-between gap-4 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
            <p>{verificationBanner}</p>
            <button
              onClick={() => setVerificationBanner(null)}
              className="text-orange-700 hover:text-orange-900"
              aria-label="Melding sluiten"
            >
              Sluiten
            </button>
          </div>
        )}

        {/* Compliance Section */}
        <ComplianceSection
          merchantData={merchantData}
          onRefresh={loadMerchantStatus}
          hasSellerActivity={listings.length > 0}
          error={merchantError}
        />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Totaal aangeboden</p>
            <p className="text-3xl font-bold text-gray-900">{listings.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">In afwachting</p>
            <p className="text-3xl font-bold text-yellow-600">
              {listings.filter(l => l.status === 'pendingAdminReview' || l.status === 'pending_verification').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Actief</p>
            <p className="text-3xl font-bold text-green-600">
              {listings.filter(l => l.status === 'active' || l.status === 'approved').length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-600 mb-1">Verkocht</p>
            <p className="text-3xl font-bold text-blue-600">
              {listings.filter(l => l.status === 'sold').length}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <Link
            to="/sell"
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-semibold hover:shadow-lg transition"
          >
            + Nieuwe cadeaubon verkopen
          </Link>
        </div>

        {/* Listings */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Mijn Aanbiedingen</h2>
          </div>

          {listings.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎁</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Nog geen aanbiedingen</h3>
              <p className="text-gray-600 mb-6">Begin met verkopen om je eerste cadeaubon aan te bieden</p>
              <Link
                to="/sell"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-semibold hover:shadow-lg transition"
              >
                Verkoop je eerste bon
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {listings.map((listing) => (
                <div key={listing.id} className="px-6 py-4 hover:bg-gray-50 transition">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{listing.brand}</h3>
                        {getStatusBadge(listing.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                        <span>Waarde: {formatPrice(listing.originalValue)}</span>
                        <span>Uitbetaling: {formatPrice(listing.sellerPayout)}</span>
                        <span>{formatDate(listing.createdAt)}</span>
                      </div>
                      {listing.status === 'rejected' && listing.verificationDetails?.rejectionReason && (
                        <p className="mt-2 text-sm text-red-600">
                          Reden: {listing.verificationDetails.rejectionReason}
                        </p>
                      )}
                       {/* Actions inline on mobile */}
                      <div className="flex items-center gap-4 mt-2">
                        {(listing.status === 'active' || listing.status === 'approved') && (
                          <Link to="/marketplace" className="text-sm text-red-500 hover:text-red-600 font-semibold">
                            Bekijk →
                          </Link>
                        )}
                        {(
                          listing.status === 'pendingAdminReview' ||
                          listing.status === 'pending_verification' ||
                          listing.status === 'active' ||
                          listing.status === 'approved'
                        ) && (
                          <button
                            onClick={() => handleCancel(listing)}
                            className="text-sm text-gray-400 hover:text-red-500 transition"
                          >
                            Annuleren
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
